import { useEffect, useState, Suspense, lazy, startTransition } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { getUserSession, checkEmailVerification } from '../services/api';
import { logout, setToken } from '../redux/reducers/authReducer';
import { clearUser, setUser } from '../redux/reducers/userReducer';
import ErrorBoundary from '../components/ErrorBoundary';
import AuthErrorBoundary from '../components/AuthErrorBoundary';
import LoadingSpinner from '../components/LoadingSpinner';
import PromoPopup, { PROMO_DISMISSED_KEY } from '../components/PromoPopup';
import { safeLog } from '../utils/security';
import { ADMIN_LAST_ORG_KEY } from '../components/shell/AdminOrgSwitcher';

// Lazy load components for better performance
const DashboardLayout = lazy(() => import('../components/Dashboard/DashboardLayout'));
const Overview = lazy(() => import('../pages/Dashboard/Overview'));
const Claims = lazy(() => import('../pages/Dashboard/Claims'));
const CallLogs = lazy(() => import('../pages/Dashboard/CallLogs'));
const Scheduling = lazy(() => import('../pages/Dashboard/Scheduling'));
const PaymentPosting = lazy(() => import('../pages/Dashboard/PaymentPosting'));
const ComingSoon = lazy(() => import('../pages/Dashboard/ComingSoon'));
const Playground = lazy(() => import('../pages/Dashboard/Playground'));
const Patients = lazy(() => import('../pages/Dashboard/Patients'));
const Payers = lazy(() => import('../pages/Dashboard/Payers'));
const Providers = lazy(() => import('../pages/Dashboard/Providers'));
const LiveCalls = lazy(() => import('../pages/Dashboard/LiveCalls'));
const Billing = lazy(() => import('../pages/Dashboard/Billing'));
const Integrations = lazy(() => import('../pages/Dashboard/Integrations'));
const Upload = lazy(() => import('../pages/Dashboard/Upload'));
const Profile = lazy(() => import('../pages/Dashboard/Profile'));
const Onboarding = lazy(() => import('../pages/Onboarding'));
const ModulePage = lazy(() => import('../pages/Modules/ModulePage'));
const LogIn = lazy(() => import('../pages/Auth/LogIn'));
const ResetPassword = lazy(() => import('../pages/Auth/ResetPassword'));
const AdminLogin = lazy(() => import('../pages/Admin/AdminLogin'));
const AdminLayout = lazy(() => import('../pages/Admin/AdminLayout'));
const AdminOrgScope = lazy(() => import('../pages/Admin/AdminOrgScope'));
const AdminOverview = lazy(() => import('../pages/Admin/AdminOverview'));
const AdminCallCosts = lazy(() => import('../pages/Admin/AdminCallCosts'));
const AdminBilling = lazy(() => import('../pages/Admin/AdminBilling'));
const AdminAnalytics = lazy(() => import('../pages/Admin/AdminAnalytics'));
const AdminOrganizations = lazy(() => import('../pages/Admin/AdminOrganizations'));
const AdminOrganizationDetail = lazy(() => import('../pages/Admin/AdminOrganizationDetail'));
const AdminDecisionTree = lazy(() => import('../pages/Admin/AdminDecisionTree'));
const AdminDecisionTreeAnalytics = lazy(() => import('../pages/Admin/AdminDecisionTreeAnalytics'));
const AdminVoiceAgents = lazy(() => import('../pages/Admin/AdminVoiceAgents'));
const AdminUpload = lazy(() => import('../pages/Admin/AdminUpload'));
const AdminLiveCalls = lazy(() => import('../pages/Admin/AdminLiveCalls'));
const AdminLiveCallDetail = lazy(() => import('../pages/Admin/AdminLiveCallDetail'));
const AdminOrgClaims = lazy(() => import('../pages/Admin/AdminOrgClaims'));
const AdminPatients = lazy(() => import('../pages/Admin/AdminPatients'));
const AdminPayers = lazy(() => import('../pages/Admin/AdminPayers'));
const AdminProviders = lazy(() => import('../pages/Admin/AdminProviders'));
const AdminCallLogs = lazy(() => import('../pages/Admin/AdminCallLogs'));
const AdminProfile = lazy(() => import('../pages/Admin/AdminProfile'));
const AdminInsights = lazy(() => import('../pages/Admin/AdminInsights'));
const AdminPlaygroundPage = lazy(() => import('../pages/Admin/AdminPlayground'));
const AdminIntegrations = lazy(() => import('../pages/Admin/AdminIntegrations'));
const AdminEraInbox = lazy(() => import('../pages/Admin/AdminEraInbox'));
// PersonaPlex Training Studio — full-screen C10 surface OUTSIDE AdminLayout
// (C10 must not leak into the C8 admin chrome). Still admin-JWT-gated.
const StudioLayout = lazy(() => import('../pages/Admin/studio/StudioLayout'));
const StudioOverview = lazy(() => import('../pages/Admin/studio/StudioOverview'));
const StudioIngest = lazy(() => import('../pages/Admin/studio/StudioIngest'));
const StudioCalls = lazy(() => import('../pages/Admin/studio/StudioCalls'));
const StudioCallWorkbench = lazy(() => import('../pages/Admin/studio/StudioCallWorkbench'));
const StudioReview = lazy(() => import('../pages/Admin/studio/StudioReview'));
const StudioTeam = lazy(() => import('../pages/Admin/studio/StudioTeam'));
const StudioDatasets = lazy(() => import('../pages/Admin/studio/StudioDatasets'));
const StudioTraining = lazy(() => import('../pages/Admin/studio/StudioTraining'));
const StudioSecurity = lazy(() => import('../pages/Admin/studio/StudioSecurity'));


// Component to handle auth validation on app load
const AuthValidator: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, token } = useSelector((state: RootState) => state.auth);
  const userData = useSelector((state: RootState) => state.user.userData);

  // List of auth pages where users should be allowed
  const authPages = ['/auth/login', '/auth/reset-password'];
  const isOnAuthPage = authPages.some(page => location.pathname.startsWith(page));

  useEffect(() => {
    const validateSession = async () => {
      const storedToken = sessionStorage.getItem('authToken');
      
      // If we have a token in storage but not in Redux, restore the auth state
      if (storedToken && !isAuthenticated) {
        try {
          const response = await getUserSession();
          if (response.success) {
            // Session is valid, restore auth state
            dispatch(setToken(storedToken));
            // Try to get user data if we don't have it
            try {
              const verificationResponse = await checkEmailVerification();
              dispatch(setUser({
                user_id: verificationResponse.data.uid,
                email: verificationResponse.data.email,
                first_name: '',
                last_name: '',
                uid: verificationResponse.data.uid,
                is_verified: verificationResponse.data.email_verified,
              }));
            } catch (userErr) {
              // If we can't get user data, that's okay, we'll get it on next login
              safeLog.warn('Could not fetch user data during session restore:', userErr);
            }
          } else {
            // Session is invalid, clear everything
            startTransition(() => {
              sessionStorage.removeItem('authToken');
              dispatch(logout());
              dispatch(clearUser());
            });
          }
        } catch (error) {
          // API call failed, clear everything
          startTransition(() => {
            sessionStorage.removeItem('authToken');
            dispatch(logout());
            dispatch(clearUser());
          });
        }
      } else if (!storedToken && isAuthenticated) {
        // We have Redux state but no token in storage, clear Redux
        dispatch(logout());
        dispatch(clearUser());
      }
    };

    // Only run once on mount
    validateSession();
  }, [dispatch, isAuthenticated, token, navigate, userData, isOnAuthPage]);

  return null;
};

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  return <>{children}</>;
};

const ADMIN_HOSTNAME = 'admin.medobill.com';

const PromoDetector: React.FC<{
  onPromoDetected: (code: string) => void;
}> = ({ onPromoDetected }) => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const promoCode = params.get('promo');

    if (promoCode) {
      sessionStorage.setItem('promoCode', promoCode);

      const dismissed = sessionStorage.getItem(PROMO_DISMISSED_KEY);
      if (!dismissed) {
        onPromoDetected(promoCode);
      }
    }
  }, [location.search, onPromoDetected]);

  return null;
};

const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const adminToken = sessionStorage.getItem('adminAuthToken');
  if (!adminToken) {
    return <Navigate to="/admin/login" replace />;
  }
  return <>{children}</>;
};

const AdminDomainGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAdminHost = window.location.hostname === ADMIN_HOSTNAME;

  useEffect(() => {
    if (!isAdminHost) {
      const { pathname, search, hash } = window.location;
      window.location.href = `https://${ADMIN_HOSTNAME}${pathname}${search}${hash}`;
    }
  }, [isAdminHost]);

  if (!isAdminHost) {
    return <LoadingSpinner message="Redirecting to admin portal..." />;
  }

  return <>{children}</>;
};

/**
 * Read the last-selected admin org id from localStorage. Returns null
 * when the storage isn't accessible (private mode / SSR) or empty.
 */
const readLastAdminOrgId = (): string | null => {
  try {
    return localStorage.getItem(ADMIN_LAST_ORG_KEY);
  } catch {
    return null;
  }
};

/**
 * `/admin` index redirect. If the admin has a remembered last-org,
 * jump them straight to that org's overview. Otherwise drop them on
 * the org picker (`/admin/organizations`) — no empty-state limbo.
 */
const AdminEntryRedirect: React.FC = () => {
  const lastId = readLastAdminOrgId();
  if (lastId) {
    return <Navigate to={`/admin/orgs/${lastId}/overview`} replace />;
  }
  return <Navigate to="/admin/organizations" replace />;
};

/**
 * Legacy admin-URL redirect. Used to keep old bookmarks alive (e.g.
 * `/admin/billing` → `/admin/orgs/<lastId>/billing`). When there's no
 * remembered org, fall through to the picker.
 */
const LegacyAdminRedirect: React.FC<{ tail: string }> = ({ tail }) => {
  const location = useLocation();
  const lastId = readLastAdminOrgId();
  if (!lastId) {
    return <Navigate to="/admin/organizations" replace />;
  }
  const target = `/admin/orgs/${lastId}/${tail}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
};

/**
 * Legacy `/admin/organizations/:orgId` deep links land on the new
 * Profile page (which subsumes the old AdminOrganizationDetail tabs).
 * The query string is preserved so existing `?tab=workflow` style
 * links still surface the right sub-section once the Profile page
 * grows tab support in Phase D.
 */
const LegacyOrgDetailRedirect: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();
  if (!orgId) {
    return <Navigate to="/admin/organizations" replace />;
  }
  return (
    <Navigate
      to={`/admin/orgs/${orgId}/profile${location.search}${location.hash}`}
      replace
    />
  );
};

const AppRoutes: React.FC = () => {
  const isAdminHost = window.location.hostname === ADMIN_HOSTNAME;
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [showPromoPopup, setShowPromoPopup] = useState(false);

  const handlePromoDetected = (code: string) => {
    setPromoCode(code);
    setShowPromoPopup(true);
  };

  const handlePromoClose = () => {
    setShowPromoPopup(false);
  };

  return (
    <ErrorBoundary>
      <Router>
        <AuthValidator />
        <PromoDetector onPromoDetected={handlePromoDetected} />
        {promoCode && (
          <PromoPopup
            promoCode={promoCode}
            isOpen={showPromoPopup}
            onClose={handlePromoClose}
          />
        )}
        <Suspense fallback={<LoadingSpinner message="Loading application..." />}>
          <Routes>
            {/* Make Onboarding the default route for the app */}
            <Route
              path="/"
              element={isAdminHost ? <Navigate to="/admin/login" replace /> : <Onboarding />}
            />
            <Route
              path="/modules/:slug"
              element={isAdminHost ? <Navigate to="/admin/login" replace /> : <ModulePage />}
            />
            <Route
              path="/admin/login"
              element={
                <AdminDomainGate>
                  <AdminLogin />
                </AdminDomainGate>
              }
            />
            {/* PersonaPlex Training Studio — its own C10 shell, deliberately
                outside <AdminLayout> so the aurora surface never mixes with
                the C8 console chrome. Same domain gate + admin JWT guard. */}
            <Route
              path="/admin/studio"
              element={
                <AdminDomainGate>
                  <AdminProtectedRoute>
                    <StudioLayout />
                  </AdminProtectedRoute>
                </AdminDomainGate>
              }
            >
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<StudioOverview />} />
              <Route path="ingest" element={<StudioIngest />} />
              <Route path="calls" element={<StudioCalls />} />
              <Route path="calls/:callId" element={<StudioCallWorkbench />} />
              <Route path="review" element={<StudioReview />} />
              <Route path="team" element={<StudioTeam />} />
              <Route path="datasets" element={<StudioDatasets />} />
              <Route path="training" element={<StudioTraining />} />
              <Route path="security" element={<StudioSecurity />} />
              <Route path="*" element={<Navigate to="overview" replace />} />
            </Route>
            {/* Admin shell — see documentation.md §19h. Every admin URL is
                rendered inside a single `<AdminLayout>` so the topbar /
                sidebar / frame / padding stay constant across the
                org-picker and org-scoped pages. The org-scoped subtree
                lives under `/admin/orgs/:orgId/*` and is wrapped by
                `<AdminOrgScope>` to provide org context to children. */}
            <Route
              path="/admin"
              element={
                <AdminDomainGate>
                  <AdminProtectedRoute>
                    <AdminLayout />
                  </AdminProtectedRoute>
                </AdminDomainGate>
              }
            >
              <Route index element={<AdminEntryRedirect />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              {/* Legacy `/admin/organizations/:orgId` deep link →
                  /admin/orgs/:orgId/profile (tab preserved). Kept
                  alive so old Slack threads / bookmarks don't 404. */}
              <Route
                path="organizations/:orgId"
                element={<LegacyOrgDetailRedirect />}
              />
              {/* Legacy top-level admin pages — redirect into the
                  last-used-org URL or to the org picker. They render
                  inside the shell briefly before the redirect fires,
                  which is invisible in practice. */}
              <Route
                path="overview"
                element={<LegacyAdminRedirect tail="overview" />}
              />
              <Route
                path="calls"
                element={<LegacyAdminRedirect tail="calls" />}
              />
              <Route
                path="billing"
                element={<LegacyAdminRedirect tail="billing" />}
              />
              <Route
                path="analytics"
                element={<LegacyAdminRedirect tail="analytics" />}
              />
              <Route
                path="upload-claims"
                element={<LegacyAdminRedirect tail="upload" />}
              />
              <Route
                path="voice-agents"
                element={<LegacyAdminRedirect tail="voice-agents" />}
              />
              <Route
                path="decision-tree"
                element={<LegacyAdminRedirect tail="decision-tree" />}
              />
              <Route
                path="decision-tree/analytics"
                element={<LegacyAdminRedirect tail="decision-tree/analytics" />}
              />
              <Route
                path="live-calls"
                element={<LegacyAdminRedirect tail="live-calls" />}
              />
              <Route
                path="live-calls/:callId"
                element={<AdminLiveCallDetail />}
              />
              {/* Org-scoped admin tree — every page lives here. */}
              <Route path="orgs/:orgId" element={<AdminOrgScope />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<AdminOverview />} />
                <Route path="live-calls" element={<AdminLiveCalls />} />
                <Route
                  path="live-calls/:callId"
                  element={<AdminLiveCallDetail />}
                />
                <Route path="call-logs" element={<AdminCallLogs />} />
                <Route path="upload" element={<AdminUpload />} />
                <Route path="playground" element={<AdminPlaygroundPage />} />
                <Route path="claims" element={<AdminOrgClaims />} />
                <Route path="providers" element={<AdminProviders />} />
                <Route path="patients" element={<AdminPatients />} />
                <Route path="payers" element={<AdminPayers />} />
                <Route path="integrations" element={<AdminIntegrations />} />
                <Route path="era-inbox" element={<AdminEraInbox />} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="profile" element={<AdminProfile />} />
                <Route path="voice-agents" element={<AdminVoiceAgents />} />
                <Route path="decision-tree" element={<AdminDecisionTree />} />
                <Route
                  path="decision-tree/analytics"
                  element={<AdminDecisionTreeAnalytics />}
                />
                <Route path="calls" element={<AdminCallCosts />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="insights" element={<AdminInsights />} />
                {/* Defensive: anything unknown under /admin/orgs/:id/*
                    falls back to overview rather than 404'ing. */}
                <Route path="*" element={<Navigate to="overview" replace />} />
              </Route>
              {/* Keep AdminOrganizationDetail importable even though
                  its URL is now a redirect. It is referenced from a
                  handful of tab links inside the new Profile page
                  until those links are migrated. */}
              <Route
                path="_legacy/org-detail/:orgId"
                element={<AdminOrganizationDetail />}
              />
            </Route>
            <Route path="/auth/login" element={
              <AuthErrorBoundary>
                <LogIn />
              </AuthErrorBoundary>
            } />
            <Route path="/auth/reset-password" element={
              <AuthErrorBoundary>
                <ResetPassword />
              </AuthErrorBoundary>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Overview />} />
              <Route path="overview" element={<Overview />} />
              <Route path="claim-history" element={<Claims />} />
              <Route path="payment-posting" element={<PaymentPosting />} />
              <Route path="call-logs" element={<CallLogs />} />
              <Route path="scheduling" element={<Scheduling />} />
              <Route path="playground" element={<Playground />} />
              <Route path="patients" element={<Patients />} />
              <Route path="payers" element={<Payers />} />
              <Route path="providers" element={<Providers />} />
              <Route path="live-calls" element={<LiveCalls />} />
              <Route path="billing" element={<Billing />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="upload" element={<Upload />} />
              <Route path="profile" element={<Profile />} />
              <Route
                path="medical-coding"
                element={
                  <ComingSoon
                    eyebrow="Revenue Cycle"
                    title="Medical Coding"
                    description="Assign and review ICD-10, CPT, and HCPCS codes before charges are submitted."
                    bullets={[
                      'AI-assisted code suggestions from encounter notes',
                      'Coder review queue with edit flags',
                      'Code-level scrubbing before claim submission',
                    ]}
                  />
                }
              />
              <Route
                path="check-in"
                element={
                  <ComingSoon
                    eyebrow="Revenue Cycle"
                    title="Patient Check-In"
                    description="Front-desk verification of demographics and insurance at the point of arrival."
                    bullets={[
                      'Demographic + insurance confirmation',
                      'Copay collection prompts',
                      'Real-time eligibility at arrival',
                    ]}
                  />
                }
              />
              <Route
                path="charge-capture"
                element={
                  <ComingSoon
                    eyebrow="Revenue Cycle"
                    title="Charge Capture"
                    description="Translate documented services into billable charges and route them into claims."
                    bullets={[
                      'Service-to-charge mapping',
                      'Missing charge detection',
                      'Charge review before claim creation',
                    ]}
                  />
                }
              />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
};

export default AppRoutes;